import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../utils/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      authAPI.getMe()
        .then(res => {
          setUser(res.data);
          setLoading(false);
        })
        .catch(() => {
          localStorage.removeItem('token');
          setToken(null);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (email, password) => {
    const response = await authAPI.login(email, password);
    const data = response.data;
    
    // Verificar si requiere 2FA
    if (data.requires_2fa) {
      return {
        requires_2fa: true,
        user_id: data.user_id,
        user_email: data.user_email,
        message: data.message
      };
    }
    
    // Verificar si la contraseña expiró
    if (data.password_expired) {
      // Retornar información de expiración sin guardar token
      return {
        password_expired: true,
        user_id: data.user_id,
        user_email: data.user_email,
        password_info: data.password_info
      };
    }
    
    // Login normal
    const { access_token, user: userData } = data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    
    // Retornar userData con información de advertencia si existe
    return userData;
  };

  // Manual auth setter for 2FA flow
  const setAuth = ({ isAuthenticated, user: userData, token: newToken }) => {
    if (isAuthenticated && newToken) {
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
    } else {
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const updateUser = (updatedData) => {
    setUser(prev => ({ ...prev, ...updatedData }));
  };

  const refreshUser = async () => {
    try {
      const res = await authAPI.getMe();
      setUser(res.data);
    } catch (error) {
      console.error('Error refreshing user:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, isAuthenticated: !!user, updateUser, refreshUser, setAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
